import { useSetAtom } from 'jotai';
import { ReactNode, useEffect } from 'react';
import { Box } from '@mui/material';
import * as state from '@/components/AssetLabelling/state';
import {
  useGetStructureLocationsForAssetLabellingDataLoaderQuery,
  useGetAllLabelAnnotationsQuery,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { useCuboidSubscriptions } from '../hooks/useCuboidSubscriptions';

type Props = {
  structureId: string;
  children?: ReactNode;
};

export const DataLoader = ({ structureId, children }: Props) => {
  const setLocations = useSetAtom(state.locations);
  const setLabelAnnotations = useSetAtom(state.labelAnnotations);

  useCuboidSubscriptions();

  const locationsResult = useGetStructureLocationsForAssetLabellingDataLoaderQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const { data, loading, error } = locationsResult;
    if (loading) return;
    if (error) {
      throw new Error('Unable to fetch structure locations');
    }
    setLocations(data?.allLocations);
  }, [setLocations, locationsResult]);

  const labelAnnotationsResult = useGetAllLabelAnnotationsQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const { data, loading, error } = labelAnnotationsResult;
    if (loading) return;
    if (error) {
      throw new Error('Unable to fetch structure label-annotations');
    }
    setLabelAnnotations(data?.allLabelAnnotations);
  }, [labelAnnotationsResult, setLabelAnnotations]);

  const isLoading = locationsResult.loading || labelAnnotationsResult.loading;

  return (
    <>
      {isLoading ? (
        <Box sx={{ position: 'relative', top: '40vh' }}>
          <PageLoader />
        </Box>
      ) : (
        children
      )}
    </>
  );
};
